import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { NgbModal, NgbToastModule } from '@ng-bootstrap/ng-bootstrap';
import { AlertModalComponent } from '../../shared/alert-modal/alert-modal.component';
import {
  MsalService, MsalModule, MsalBroadcastService, MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest,
  EventMessage, EventType,
} from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import { LoadingServiceService } from '../../services/loadingService/loading-service.service';
import { LoginService } from '../../services/login-service/login.service';
import { environment } from '../../../../environments/environment';
import { DataShareService } from '../../services/data_share/data-share.service';
import { ToastService } from '../../services/toast/toast.service';
import { AuthService } from './../../services/auth/auth.service';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, MsalModule, NgbToastModule, ReactiveFormsModule, FormsModule],
  templateUrl: './login-page.component.html',
  styleUrl: './login-page.component.scss'
})
export class LoginPageComponent implements OnInit, OnDestroy {
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  selectedNode: string = 'Select Node';
  selectedEnv: string = 'Select ENV';
  isLoading: boolean = false;

  // Use nodeScopes from environment
  private nodeScopes = environment.nodeScopes;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    public loadService: LoadingServiceService,
    private loginService: LoginService,
    private dataSharingService: DataShareService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    @Inject(PLATFORM_ID) private platformId: Object,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    public toastService: ToastService,
    private cdRef: ChangeDetectorRef,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const savedNode = sessionStorage.getItem('selectedNode')
      if(savedNode === 'Select Node'){
        sessionStorage.removeItem('selectedNode')
      }
      const sessionId = sessionStorage.getItem('Session_Id')
      const returnUrl = this.router.parseUrl(this.router.url).queryParams['returnUrl'];

      if(sessionId && returnUrl){
        this.router.navigateByUrl(returnUrl);
      } else if ( sessionId){
        this.router.navigate(['/dashboardpage'])
      }

      this.isIframe = window !== window.parent && !window.opener;
      try {
        this.setupMsalListeners();
        this.authService.handleRedirectCallback();
      } catch (error) {
        this.toastService.showToast(`Error during Login Initialization: ${error}`,
          { classname: "bg-danger text-light" });
      }
    }
  }

  private setupMsalListeners(): void {
    this.msalService.instance.enableAccountStorageEvents();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          msg.eventType === EventType.ACCOUNT_ADDED ||
          msg.eventType === EventType.ACCOUNT_REMOVED),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        console.log("MSAL account event:", result.eventType);
        this.setLoginDisplay();
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
    this.cdRef.detectChanges();
  }

  checkAndSetActiveAccount() {
    let activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      let accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  onNodeSelect(event: Event) {
    const nodeName = (event.target as HTMLSelectElement).value;
    if(nodeName && nodeName !== 'Select Node'){
      this.selectedNode = nodeName
      this.cdRef.detectChanges();
    }
    this.cdRef.detectChanges();
  }

  loginRedirect() {
    // Validate both Node and ENV selections
    if (this.selectedNode === 'Select Node' || !['DEV', 'QAS'].includes(this.selectedEnv)) {
      const missing = [];
      if (this.selectedNode === 'Select Node') missing.push('Node');
      if (!['DEV', 'QAS'].includes(this.selectedEnv)) missing.push('ENV');
      
      this.toastService.showToast(`Please select ${missing.join(' and ')} to login`, {
        classname: "bg-warning text-dark"
      });
      return;
    }

    // Save selections and proceed
    this.dataSharingService.selectedNodeValue(this.selectedNode);
    sessionStorage.setItem('selectedEnv', this.selectedEnv);
    sessionStorage.setItem('selectedNode', this.selectedNode);

    // Use scopes from environment configuration
    const request: RedirectRequest = {
      scopes: this.nodeScopes[this.selectedNode as keyof typeof this.nodeScopes],
      extraScopesToConsent: [],
    };

    this.msalService.loginRedirect(request);
  }

  // Helper method to get available nodes (useful for dropdowns)
  getAvailableNodes(): string[] {
    return Object.keys(environment.nodeScopes);
  }

  // Helper method to get scopes for a specific node
  getScopesForNode(node: string): string[] {
    return environment.nodeScopes[node as keyof typeof environment.nodeScopes] || [];
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}